import { useMemo } from 'react';
import { DataSource } from '@weave/schema-gen-ts/dist/schemas/syncapp/integrations-service/datasource.pb';
import { DataSourcesQueries } from '@frontend/api-data-sources';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { Text, Truncated } from '@frontend/design-system';

type UseDataSourceColumnProps<T> = {
  /**
   * Field name to extract sourceId from a data item (e.g., 'sourceId')
   */
  sourceIdField: keyof T;
};

type DataSourceColumnConfig<T> = {
  /**
   * Whether to show the source column (true if multiple sources exist)
   */
  shouldShowColumn: boolean;
  /**
   * Lookup object for source retrieval by sourceId
   */
  dataSourcesLookup: Record<string, DataSource>;
  /**
   * Cell renderer function for rendering data source information
   */
  cellRenderer: (cellValue: string | undefined) => JSX.Element | null;
  /**
   * Accessor function to extract sourceId from data row
   */
  accessor: (row: T) => string | undefined;
};

/**
 * Custom hook for creating a reusable data source cell renderer for tables
 * Provides optimized lookup and cell rendering logic for data source columns
 */
export const useDataSourceColumn = <T,>({ sourceIdField }: UseDataSourceColumnProps<T>): DataSourceColumnConfig<T> => {
  // Check if location has integrations
  const { isIntegratedOffice, hasMultiplePmsSources } = DataSourcesQueries.useGetAccessibleIntegrations();

  // Fetch data sources from the API
  const { data: dataSourcesData } = SchedulerV3.Helpers.useGetDataSourcesForLocation({
    isEnabled: isIntegratedOffice && hasMultiplePmsSources,
  });

  const dataSourcesLookup = useMemo(() => {
    if (!dataSourcesData?.dataSources) return {};

    return dataSourcesData.dataSources.reduce((acc: Record<string, DataSource>, source: DataSource) => {
      if (source.sourceId) {
        acc[source.sourceId] = source;
      }
      return acc;
    }, {});
  }, [dataSourcesData?.dataSources]);

  // Cell renderer function
  const cellRenderer = useMemo(() => {
    const DataSourceCell = (cellValue: string | undefined) => {
      if (!cellValue) return null;

      const matchingSource = dataSourcesLookup[cellValue];

      return matchingSource ? (
        <div css={{ width: '100%' }}>
          <Truncated as={Text} size='medium'>
            {matchingSource.sourceName || ''}
          </Truncated>
          <Truncated as={Text} size='medium' color='subdued'>
            {matchingSource.integration?.displayName || ''}
          </Truncated>
        </div>
      ) : null;
    };

    DataSourceCell.displayName = 'DataSourceCell';
    return DataSourceCell;
  }, [dataSourcesLookup]);

  // Accessor function for extracting sourceId from data row
  const accessor = useMemo(() => (row: T) => row?.[sourceIdField] as string | undefined, [sourceIdField]);

  return {
    shouldShowColumn: isIntegratedOffice && hasMultiplePmsSources,
    dataSourcesLookup,
    cellRenderer,
    accessor,
  };
};
